import React from 'react';
import { Contact } from '../../../types/Contact';
import { User as UserIcon, Phone, Mail, Globe } from 'lucide-react';
import { formatPhoneDisplay, getSelectedContactInfo, getWebsiteDisplay, copyField } from '../utils/contactHelpers';

interface ContactProfileProps {
    contact: Contact;
    setIsGalleryOpen: (open: boolean) => void;
    handleAddDetails: () => void;
    showNotification?: (message: string, type: 'success' | 'error') => void;
}

export default function ContactProfile({
    contact,
    setIsGalleryOpen,
    handleAddDetails,
    showNotification
}: ContactProfileProps) {
    const selectedContact = getSelectedContactInfo(contact);
    const websiteDisplay = getWebsiteDisplay(selectedContact.website?.value);

    return (
        <>
            {/* Profile Section */}
            <div className="flex flex-col items-center mt-6 md:mt-8">
                <div
                    className={`w-28 h-28 md:w-32 md:h-32 lg:w-36 lg:h-36 bg-gray-700 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0 mx-auto shadow-lg relative mb-4 ${contact.profile_image_url ? 'cursor-pointer hover:opacity-90' : ''}`}
                    onClick={() => setIsGalleryOpen(true)}
                    role={contact.profile_image_url ? 'button' : undefined}
                    aria-label={contact.profile_image_url ? 'View profile image' : undefined}
                >
                    {contact.profile_image_url ? (
                        <img
                            src={contact.profile_image_url}
                            alt={`${contact.first_name} ${contact.last_name}`}
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center">
                            <UserIcon className="w-12 h-12 text-gray-400" />
                        </div>
                    )}
                </div>

                <button
                    type="button"
                    onClick={() => copyField('Name', `${(contact.first_name || '').trim()} ${(contact.last_name || '').trim()}`.trim(), showNotification)}
                    title="Click to copy"
                    aria-label="Copy full name"
                    className="text-2xl font-bold text-white mb-1 max-w-[90%] md:max-w-[70%] mx-auto whitespace-normal break-words text-center hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-600 rounded"
                >
                    {`${contact.first_name || ''} ${contact.last_name || ''}`.trim() || '—'}
                </button>
                <p className="text-gray-300 mb-2 text-center max-w-[90%] md:max-w-[70%] mx-auto whitespace-normal break-words">
                    {contact.title && contact.company
                        ? `${contact.title} at ${contact.company}`
                        : contact.title || contact.company || 'No title'
                    }
                </p>
                {/* Quick contact row (personal-first with fallback) */}
                {(selectedContact.phone?.value || selectedContact.email?.value || selectedContact.website?.value || selectedContact.linkedin) && (
                    <div className="w-full max-w-xl mx-auto mb-4">
                        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm text-gray-400">
                            {selectedContact.phone?.value && (
                                <a
                                    href={`tel:${selectedContact.phone.value}`}
                                    className="inline-flex items-center gap-1 hover:text-gray-200 transition-colors"
                                    title={selectedContact.phone.source === 'company' ? 'Company phone (fallback)' : 'Personal phone'}
                                >
                                    <Phone className="w-3.5 h-3.5 text-green-400/70" />
                                    <span className="tabular-nums">{formatPhoneDisplay(selectedContact.phone.value) || selectedContact.phone.value}</span>
                                </a>
                            )}
                            {selectedContact.email?.value && (
                                <a
                                    href={`mailto:${selectedContact.email.value}`}
                                    className="inline-flex items-center gap-1 hover:text-gray-200 transition-colors"
                                    title={selectedContact.email.source === 'company' ? 'Company email (fallback)' : 'Personal email'}
                                >
                                    <Mail className="w-3.5 h-3.5 text-blue-400/70" />
                                    <span className="truncate max-w-[200px]">{selectedContact.email.value}</span>
                                </a>
                            )}
                            {selectedContact.website?.value && (
                                <a
                                    href={selectedContact.website.value.startsWith('http') ? selectedContact.website.value : `https://${selectedContact.website.value}`}
                                    target="_blank" rel="noreferrer"
                                    className="inline-flex items-center gap-1 hover:text-gray-200 transition-colors"
                                    title={selectedContact.website.source === 'company' ? 'Company website (fallback)' : 'Website'}
                                >
                                    <Globe className="w-3.5 h-3.5 text-yellow-400/70" />
                                    <span>{websiteDisplay || selectedContact.website.value}</span>
                                </a>
                            )}
                            {selectedContact.linkedin && (
                                <a
                                    href={selectedContact.linkedin}
                                    target="_blank" rel="noreferrer"
                                    className="inline-flex items-center gap-1 hover:text-gray-200 transition-colors"
                                    title="LinkedIn"
                                >
                                    <Globe className="w-3.5 h-3.5 text-blue-400/70" />
                                    <span>LinkedIn</span>
                                </a>
                            )}
                        </div>
                    </div>
                )}

                {/* Primary Action */}
                <div className="w-full flex justify-center">
                    <button
                        onClick={handleAddDetails}
                        className="inline-flex items-center justify-center px-6 py-3 border transition-colors font-semibold text-sm tracking-wide bg-gray-800 text-white border-gray-700 hover:bg-gray-700 min-w-[160px] uppercase"
                    >
                        Add Details
                    </button>
                </div>
            </div>
        </>
    );
}